import { Component, Vue, Watch } from 'nuxt-property-decorator';
import SearchForQuery from '@/components/common/UniversalSearch/apollo/SearchFor.query';
import CreateWarRoomRatingMutation from '~/components/common/UniversalSearch/apollo/CreateWarRoomRating.mutation';
import DestroyWarRoomRatingMutation from '~/components/common/UniversalSearch/apollo/DestroyWarRoomRating.mutation';
import SaveSalesGptSummary from '@/components/common/UniversalSearch/apollo/SaveSalesGptSummary.mutation';
import { SearchSummaryType } from '@/types/graphql';
import type { UniversalSearchResult } from '@/components/common/UniversalSearch/types';
import type { SalesGptQuery, SearchInfo } from '@/types/graphql';
import { textSteamToHtml } from '~/components/common/UniversalSearch/utils';

const MIN_CHARS = 2;
const PER_PAGE = 15;

@Component({
  // @ts-ignore
  apollo: {
    search: {
      query: SearchForQuery,
      variables() {
        return {
          ...this.queryVariables,
        };
      },
      error(e) {
        this.$notice.error('Something went wrong. Please try again.');
        this.$sentryUtils.error(e, 'salesgpt_search_error');
      },
      update(data: { search: SearchInfo }) {
        this.results = data.search.results;
        this.salesGptQuery = data.search.salesGptQuery;
      },
      skip() {
        return !this.isValidQuery;
      },
      prefetch: false,
    },
  },
})
export default class UniversalSearchMixin extends Vue {
  protected readonly minSearchChars = MIN_CHARS;
  protected searchTerm = '';
  protected results: UniversalSearchResult[] = [];
  protected salesGptQuery: SalesGptQuery | null = null;
  protected isRefetching: boolean = false;
  protected shouldBeRefetched: boolean = false;

  get sessionId() {
    return this.salesGptQuery?.sessionId;
  }

  get query() {
    return this.searchTerm.trim();
  }

  get queryVariables() {
    return {
      query: this.query,
      produceAiSummary: true,
      perPage: PER_PAGE,
    };
  }

  get isSearching() {
    if (!process.client) return true;
    return this.$apollo.queries.search.loading || this.shouldBeRefetched;
  }

  get isValidQuery() {
    return this.query.length >= this.minSearchChars;
  }

  get isResultsVisible() {
    if (!this.isValidQuery || this.shouldBeRefetched) return false;
    return this.results.length > 0;
  }

  get hasInconsistentCache(): boolean {
    return this.hasSummaryInCachedSearch;
  }

  get hasSummaryInCachedSearch() {
    try {
      const cachedSearchResult = this.$apollo
        .getClient()
        // @ts-expect-error data.get method is not documented and typed in this version of apollo-client
        .cache.data.get(`$SearchInfo:${encodeURIComponent(`${this.queryVariables.query}`)}.salesGptQuery`);

      return Boolean(cachedSearchResult?.summary);
    } catch (e) {
      this.$sentryUtils.error(e);
    }

    return false;
  }

  async refetchSearch() {
    try {
      this.isRefetching = true;
      await this.$apollo.queries.search?.refetch();
      this.shouldBeRefetched = false;
    } catch (e) {
      this.$sentryUtils.error(e);
    } finally {
      this.isRefetching = false;
      this.shouldBeRefetched = false;
    }
  }

  get isNothingFound() {
    if (this.isSearching) return false;
    if (this.isResultsVisible) return false;
    if (this.isSummaryVisible) return false;
    return this.isValidQuery;
  }

  get isSummaryVisible() {
    if (this.isSearching) return false;
    return (
      (this.salesGptQuery?.sources?.nodes.length && this.sessionId) ||
      this.salesGptQuery?.summaryType == SearchSummaryType.Certified ||
      this.salesGptQuery?.wasExternalDataUsed ||
      this.salesGptQuery?.wasCompanyPagesDataUsed ||
      this.salesGptQuery?.summaryType == SearchSummaryType.CreatePost
    );
  }

  async setRating(rating: Record<string, any>) {
    const { value, id: reactableId, type: reactableType } = rating.data;
    const itemId = `${rating.item.record.__typename}:${rating.item.record.id}`;
    const isComment = reactableType === 'WarRoom::Comment';

    if (value) {
      this.$analytics.logEvent(isComment ? 'salesgpt__post__comment__rating-set' : 'salesgpt__post__rating__set', {
        id: rating.item.record.id,
        value,
        ai_summary: true,
        session_id: this.sessionId,
      });

      await this.$apollo.mutate({
        mutation: CreateWarRoomRatingMutation,
        variables: {
          input: {
            value,
            reactableId,
            reactableType,
          },
        },
        update: (cache, { data }) => {
          const query: Record<string, any> | null = cache.readQuery({
            query: SearchForQuery,
            variables: this.queryVariables,
          });
          const entity = data?.createRating?.reactable;
          if (!query || !entity) return;

          const source = query.search.salesGptQuery.sources.nodes.find(
            (node: Record<string, any>) => `${node.record.__typename}:${node.record.id}` === itemId,
          );
          source.record.totalRating = entity.totalRating;
          source.record.currentUserRating = entity.currentUserRating;
          cache.writeQuery({ query: SearchForQuery, variables: this.queryVariables, data: query });
        },
      });
    } else {
      this.$analytics.logEvent(isComment ? 'salesgpt__post__comment__rating-unset' : 'salesgpt__post__rating__unset', {
        id: rating.item.record.id,
        ai_summary: true,
        session_id: this.sessionId,
      });

      await this.$apollo.mutate({
        mutation: DestroyWarRoomRatingMutation,
        variables: {
          input: {
            reactableId,
            reactableType,
          },
        },
        update: (cache, { data }) => {
          const query: Record<string, any> | null = cache.readQuery({
            query: SearchForQuery,
            variables: this.queryVariables,
          });
          const entity = data?.destroyRating?.reactable;
          if (!query || !entity) return;

          const source = query.search.salesGptQuery.sources.nodes.find(
            (node: Record<string, any>) => `${node.record.__typename}:${node.record.id}` === itemId,
          );
          source.record.totalRating = entity.totalRating;
          source.record.currentUserRating = entity.currentUserRating;
          cache.writeQuery({ query: SearchForQuery, variables: this.queryVariables, data: query });
        },
      });
    }
  }

  persistSummary(summary: string) {
    this.$apollo.mutate({
      mutation: SaveSalesGptSummary,
      variables: {
        input: {
          sessionId: this.sessionId,
          summary: textSteamToHtml(summary),
        },
      },
      update: (store, { data: { saveSalesGptSummary } }) => {
        const query = {
          query: SearchForQuery,
          variables: this.queryVariables,
        };
        const data = store.readQuery<{ search: SearchInfo }>(query);
        if (!data?.search?.salesGptQuery) {
          return;
        }
        data.search.salesGptQuery = <SalesGptQuery>{
          ...data.search.salesGptQuery,
          // We saved the summary from response just to be sure that it is the same as we sent
          summary: saveSalesGptSummary.summary,
        };
        store.writeQuery({ ...query, data: data });
      },
    });
  }

  @Watch('query')
  handleQueryChange() {
    this.resetState();
  }

  resetState() {
    this.results = [];
    this.salesGptQuery = null;
  }
}
